import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ThemeProvider } from "@/components/ThemeProvider";
import { UserAccountProvider } from "@/contexts/UserAccountContext";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";

// Admin Password Control System Pages
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminOperador = lazy(() => import("./pages/admin/Operador"));
const AdminGiras = lazy(() => import("./pages/admin/Giras"));
const AdminMediuns = lazy(() => import("./pages/admin/Mediuns"));
const AdminConsulentes = lazy(() => import("./pages/admin/Consulentes"));
const AdminRelatorios = lazy(() => import("./pages/admin/Relatorios"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminWhatsApp = lazy(() => import("./pages/admin/WhatsApp"));
const AdminWhatsAppConfig = lazy(() => import("./pages/admin/WhatsAppConfig"));
const Admin = lazy(() => import("./pages/Admin"));

const queryClient = new QueryClient();

const App = () => (
  <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Carregando...</div>}>
    <ThemeProvider defaultTheme="system" storageKey="tupja-theme">
      <UserAccountProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Index />} />

                {/* Login e recuperação */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verificar-email" element={<VerifyEmail />} />

                {/* Painel Administrativo */}
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/operador" element={<AdminOperador />} />
                <Route path="/admin/giras" element={<AdminGiras />} />
                <Route path="/admin/mediuns" element={<AdminMediuns />} />
                <Route path="/admin/consulentes" element={<AdminConsulentes />} />
                <Route path="/admin/relatorios" element={<AdminRelatorios />} />
                <Route path="/admin/usuarios" element={<AdminUsers />} />
                <Route path="/admin/whatsapp-operador" element={<AdminWhatsApp />} />
                <Route path="/admin/whatsapp-config-operador" element={<AdminWhatsAppConfig />} />
                <Route path="/admin/atendimentos" element={<Admin tab="appointments" />} />
                <Route path="/admin/calendario" element={<Admin tab="calendar" />} />
                <Route path="/admin/gira-semanal" element={<Admin tab="weekly" />} />
                <Route path="/admin/galeria" element={<Admin tab="gallery" />} />
                <Route path="/admin/biblioteca" element={<Admin tab="library" />} />
                <Route path="/admin/comunicados" element={<Admin tab="newsletter" />} />
                <Route path="/admin/push" element={<Admin tab="notifications" />} />
                <Route path="/admin/whatsapp" element={<Admin tab="whatsapp" />} />
                <Route path="/admin/posts" element={<Admin tab="instagram" />} />
                <Route path="/admin/membros-site" element={<Admin tab="members" />} />
                <Route path="/admin/configuracoes" element={<Admin tab="settings" />} />

                {/* Demais rotas redirecionam para a página temporária */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </UserAccountProvider>
    </ThemeProvider>
  </Suspense>
);

export default App;
